// Tab ids, labels, hash sync and counts for the Initiative Roadmap — the owner's 2026-09-03 ask: the v2 page
// stacked every surface top-to-bottom ("you have to scroll down three times"),
// so each surface now lives behind a tab. Every surface stays reachable, the
// active tab is mirrored into the URL hash (#timeline, #mindmap, #blocked, …) so a view
// can be linked or reloaded, and each tab carries a count so an empty tab is
// obvious without opening it.

import { useCallback, useEffect, useState } from 'react';
import type {
  ClarifyingQuestionsResponse,
  InitiativeState,
  IntegrityReport,
  OrgChartResponse,
  RoadmapEvent,
  RoadmapSourceListResponse,
} from '../../services/roadmapReports';
import type { SignalReport } from '../../services/signalsReports';
import { splitHash } from './roadmapHash';
import { groupEventsByRun, waitingLines } from './roadmapViewUtils';

export const ROADMAP_TABS = [
  'meeting',
  'week',
  'projects',
  'initiatives',
  'timeline',
  'mindmap',
  'blocked',
  'parked',
  'questions',
  'org',
  'history',
  'sources',
  'reports',
] as const;

export type RoadmapTab = (typeof ROADMAP_TABS)[number];

// The meeting view opens first: what is late, what is waiting and what needs
// an answer today, readable from the back of a room (owner review 4jsvwe).
export const DEFAULT_TAB: RoadmapTab = 'meeting';

export const TAB_LABELS: Record<RoadmapTab, string> = {
  // The Meeting section is three short views, not one long scroll (owner,
  // 2026-10-14: "sub tabs or something instead of infinite scroll").
  meeting: 'Agenda',
  week: 'Last week → now',
  projects: 'Where projects stand',
  initiatives: 'List',
  timeline: 'Timeline',
  mindmap: 'Mind map',
  blocked: 'Blocked',
  parked: 'Parked',
  questions: "Merlin's questions",
  org: 'Org chart',
  history: 'What changed',
  sources: 'Sources',
  reports: 'Reports',
};

/**
 * The tab bar is five sections, not eleven tabs. A section with more than one
 * surface shows them as a second, smaller row: the initiatives as a list, a
 * timeline, a mind map, what is blocked and what is parked are five views of
 * one thing; the org chart, the source ledger and the run reports are
 * reference material behind "More". Every surface keeps its own URL hash.
 */
export interface TabGroup {
  id: 'meeting' | 'initiatives' | 'questions' | 'history' | 'more';
  label: string;
  tabs: readonly RoadmapTab[];
}

export const TAB_GROUPS: readonly TabGroup[] = [
  { id: 'meeting', label: 'Meeting', tabs: ['meeting', 'week', 'projects'] },
  { id: 'initiatives', label: 'Initiatives', tabs: ['initiatives', 'timeline', 'mindmap', 'blocked', 'parked'] },
  { id: 'questions', label: "Merlin's questions", tabs: ['questions'] },
  { id: 'history', label: 'What changed', tabs: ['history'] },
  { id: 'more', label: 'More', tabs: ['org', 'sources', 'reports'] },
];

/**
 * Surfaces only leadership (exec:portal, admins) gets: the run reports carry
 * the Delta supplier pricing (owner ruling, review z66pvn). A roadmap reader
 * (roadmap:read — everyone, for now: owner ask 2026-10-15) gets every other
 * surface, and the API answers 403 for these anyway.
 */
export const LEADERSHIP_TABS: ReadonlySet<RoadmapTab> = new Set<RoadmapTab>(['reports']);
const NO_TABS: ReadonlySet<RoadmapTab> = new Set();

/** The sections as drawn, without the hidden surfaces; a section left with none is dropped. */
export function visibleGroups(hidden: ReadonlySet<RoadmapTab> = NO_TABS): TabGroup[] {
  return TAB_GROUPS.map((g) => ({ ...g, tabs: g.tabs.filter((t) => !hidden.has(t)) })).filter((g) => g.tabs.length > 0);
}

/** The section a surface belongs to. */
export function groupOf(tab: RoadmapTab, hidden: ReadonlySet<RoadmapTab> = NO_TABS): TabGroup {
  const groups = visibleGroups(hidden);
  return groups.find((g) => g.tabs.includes(tab)) ?? groups[0];
}

/** Parse a location hash ("#timeline", "#initiatives?team=Alpha+team") into a
 * tab; unknown or hidden → default tab. The "?…" part is the narrowing
 * (roadmapHash.ts) and says nothing about the tab. */
export function tabFromHash(hash: string, hidden: ReadonlySet<RoadmapTab> = NO_TABS): RoadmapTab {
  const h = splitHash(hash).tab;
  return (ROADMAP_TABS as readonly string[]).includes(h) && !hidden.has(h as RoadmapTab) ? (h as RoadmapTab) : DEFAULT_TAB;
}

/**
 * Active tab kept in sync with `window.location.hash`, without depending on a
 * router (the page is also rendered router-less in tests). Back/forward and a
 * hand-edited hash both re-select the tab. A hidden surface's hash (a
 * leadership deep link opened by a reader) lands on the default tab.
 */
export function useHashTab(hidden: ReadonlySet<RoadmapTab> = NO_TABS): [RoadmapTab, (t: RoadmapTab) => void] {
  const [tab, setTabState] = useState<RoadmapTab>(() =>
    tabFromHash(typeof window === 'undefined' ? '' : window.location.hash, hidden),
  );
  useEffect(() => {
    const onHash = () => setTabState(tabFromHash(window.location.hash, hidden));
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [hidden]);
  const setTab = useCallback((t: RoadmapTab) => {
    setTabState(t);
    // pushState, not replaceState: the browser's Back button returns to the
    // previous tab instead of leaving the roadmap (it used to land on /login).
    // The narrowing ("?team=…") rides along: a tab change keeps the filters.
    const next = `#${t}${splitHash(window.location.hash).query}`;
    if (window.location.hash !== next) {
      window.history.pushState(null, '', next);
    }
  }, []);
  return [tab, setTab];
}

/** How many things a tab holds — drives the tab badges and the empty notes. */
export function tabCounts(args: {
  visible: InitiativeState[];
  questions: ClarifyingQuestionsResponse | null;
  integrity: IntegrityReport | null;
  events: RoadmapEvent[];
  reports: SignalReport[];
  sources?: RoadmapSourceListResponse | null;
  org?: OrgChartResponse | null;
  parked?: number;
  all?: InitiativeState[];
  /** Lines on the meeting view (late + waiting + needs an answer). */
  meeting?: number;
  /** Last week → now: what the earlier report said was due by the latest one. */
  week?: number;
  /** Where projects stand: active rows in the then → now table. */
  projects?: number;
  /** What the Timeline draws (timelineSet().items); defaults to `visible`. */
  timeline?: InitiativeState[];
}): Record<RoadmapTab, number> {
  const {
    visible, questions, events, reports, sources, org, parked = 0, all = visible, meeting = 0, week = 0, projects = 0, timeline = visible,
  } = args;
  // The badge is the asks still waiting on a person or the group. An ask an
  // admin already answered or dismissed is on its way to Merlin; the
  // 'Merlin isn't sure' notes and the audit gaps are listed inside the tab
  // but are not questions anyone was asked (owner review 4jsvwe: the badge
  // said 69 while the panel said 45).
  const asks = questions
    ? [...questions.items, ...questions.group_questions].filter((q) => !q.response).length
    : 0;
  return {
    meeting,
    week,
    projects,
    initiatives: visible.length,
    timeline: timeline.filter((i) => i.status !== 'dropped').length,
    mindmap: visible.filter((i) => i.status !== 'dropped').length,
    // Initiatives held up, once each (the same list the meeting view shows).
    blocked: waitingLines(visible, all).length,
    parked,
    questions: asks,
    // The badge is how many people are NOT placed yet — what Merlin still asks about.
    org: org ? org.unplaced.length : 0,
    history: groupEventsByRun(events).length,
    // The badge is what the NEXT run will look at (unread + deferred), not the
    // whole ledger — a settled source needs no attention.
    sources: sources ? (sources.summary.unread ?? 0) + (sources.summary.deferred ?? 0) : 0,
    reports: reports.length,
  };
}

export type TabBadgeTone = 'alert' | 'neutral';

/** Surfaces whose count is something a person has to act on. */
const ALERT_TABS: ReadonlySet<RoadmapTab> = new Set<RoadmapTab>(['blocked', 'questions']);
/** Surfaces that are another view of the Initiatives list, or carry no count worth a pill. */
const UNBADGED_TABS: ReadonlySet<RoadmapTab> = new Set<RoadmapTab>(['meeting', 'week', 'projects', 'timeline', 'mindmap']);

/**
 * What a surface's pill shows. Only the ones that ask for action are red, and
 * only while they hold something; every other count is a quiet grey so the
 * tab bar does not read as a row of alarms.
 */
export function tabBadge(tab: RoadmapTab, count: number): { text: string; tone: TabBadgeTone } | null {
  if (UNBADGED_TABS.has(tab)) return null;
  return { text: String(count), tone: ALERT_TABS.has(tab) && count > 0 ? 'alert' : 'neutral' };
}

/** A section's pill: its single surface's pill, the initiatives count, or nothing for "More". */
export function groupBadge(group: TabGroup, counts: Record<RoadmapTab, number>): { text: string; tone: TabBadgeTone } | null {
  if (group.id === 'more') return null;
  return tabBadge(group.tabs[0], counts[group.tabs[0]]);
}

/**
 * Tabs the Team / Person / Status / Find bar actually narrows. On the others
 * (org chart, run history, sources, reports) it changed nothing and only
 * pushed the content down, so the bar is not drawn there.
 */
export const FILTERED_TABS: ReadonlySet<RoadmapTab> = new Set<RoadmapTab>([
  'meeting', 'week', 'projects', 'initiatives', 'timeline', 'mindmap', 'blocked', 'parked', 'questions',
]);

/** Surfaces where List / Team / Person grouping and Sort apply (the table only). */
export const GROUPED_TABS: ReadonlySet<RoadmapTab> = new Set<RoadmapTab>(['initiatives']);
/** The views that list a team's work: the selected team's definition heads them (kanban 16a22d06). */
export const TEAM_BRIEF_TABS: ReadonlySet<RoadmapTab> = new Set<RoadmapTab>(['initiatives', 'timeline', 'mindmap']);
